import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const APP_DOMAIN    = 'app.vfansmedia.com';
const MAIN_DOMAIN   = 'vfansmedia.com';
const UNLOCK_DOMAIN = 'unlocky.io';
const DOCS_DOMAIN   = 'docs.vfansmedia.com';
const UNLOCKY_DOMAIN = 'unlocky.io';

// Routes that belong to the creator app (app.vfansmedia.com)
const APP_ROUTE_PREFIXES = [
  '/login',
  '/signup',
  '/forgot-password',
  '/verify-email',
  '/create-organization',
  '/create-creator',
  '/creator/',
  '/organization/',
  '/wallet',
  '/payout-settings',
];

// Routes that belong to the buyer unlock experience (unlocky.io)
const UNLOCK_ROUTE_PREFIXES = [
  '/l/',
  '/purchase/',
];

// Routes that belong only to the public marketing site (vfansmedia.com)
const PUBLIC_ONLY_PREFIXES = [
  '/pricing',
  '/contact',
  '/support',
  '/legal',
  '/privacy-policy',
  '/terms-of-service',
  '/refund-policy',
  '/affiliate-terms',
  '/usage-policy',
  '/dmca-takedown',
  '/complaints-removal',
  '/content-provider-agreement',
];

// Routes for documentation (docs.vfansmedia.com)
const DOCS_ROUTE_PREFIXES = [
  '/docs',
];

const isAppRoute    = (p) => APP_ROUTE_PREFIXES.some(r => p === r || p.startsWith(r));
const isUnlockRoute = (p) => UNLOCK_ROUTE_PREFIXES.some(r => p === r || p.startsWith(r));
const isPublicOnly  = (p) => PUBLIC_ONLY_PREFIXES.some(r => p === r || p.startsWith(r));
const isDocsRoute   = (p) => DOCS_ROUTE_PREFIXES.some(r => p === r || p.startsWith(r));

const DomainGuard = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const hostname  = window.location.hostname;

  useEffect(() => {
    // 50ms debounce so React Router finishes rendering before we check
    const timer = setTimeout(() => {
      const path   = location.pathname;
      const search = location.search;

      // ── vfansmedia.com (main landing) ──
      if (hostname === MAIN_DOMAIN || hostname === `www.${MAIN_DOMAIN}`) {
        if (isUnlockRoute(path)) {
          window.location.replace(`https://${UNLOCK_DOMAIN}${path}${search}`);
          return;
        }
        if (isAppRoute(path)) {
          window.location.replace(`https://${APP_DOMAIN}${path}${search}`);
          return;
        }
        if (isDocsRoute(path)) {
          window.location.replace(`https://${DOCS_DOMAIN}${path}${search}`);
          return;
        }
      }

      // ── app.vfansmedia.com (creator dashboard) ──
      if (hostname === APP_DOMAIN) {
        if (path === '/') {
          // Use client-side navigation so GuestRoute can redirect
          // authenticated users to their dashboard without a full reload
          navigate('/login', { replace: true });
          return;
        }
        if (isUnlockRoute(path)) {
          window.location.replace(`https://${UNLOCK_DOMAIN}${path}${search}`);
          return;
        }
        if (isPublicOnly(path)) {
          window.location.replace(`https://${MAIN_DOMAIN}${path}${search}`);
          return;
        }
        if (isDocsRoute(path)) {
          window.location.replace(`https://${DOCS_DOMAIN}${path}${search}`);
          return;
        }
      }

      // ── unlocky.io (buyer experience) ──
      if (hostname === UNLOCK_DOMAIN) {
        if (path === '/') {
          window.location.replace(`https://${MAIN_DOMAIN}`);
          return;
        }
        if (isAppRoute(path)) {
          window.location.replace(`https://${APP_DOMAIN}${path}${search}`);
          return;
        }
        if (isPublicOnly(path)) {
          window.location.replace(`https://${MAIN_DOMAIN}${path}${search}`);
          return;
        }
        if (isDocsRoute(path)) {
          window.location.replace(`https://${DOCS_DOMAIN}${path}${search}`);
          return;
        }
      }

      // ── unlocky.io (public buyer/creator domain) ──
      if (hostname === UNLOCKY_DOMAIN || hostname === `www.${UNLOCKY_DOMAIN}`) {
        // Root → redirect to main site
        if (path === '/') {
          window.location.replace(`https://${MAIN_DOMAIN}`);
          return;
        }
        // Allow unlock routes (/l/, /purchase/, /c/) — do nothing
        if (isUnlockRoute(path)) return;
        // App routes → redirect to app domain
        if (isAppRoute(path)) {
          window.location.replace(`https://${APP_DOMAIN}${path}${search}`);
          return;
        }
        // Public/docs → redirect to main
        if (isPublicOnly(path) || isDocsRoute(path)) {
          window.location.replace(`https://${MAIN_DOMAIN}${path}${search}`);
          return;
        }
      }

      // ── docs.vfansmedia.com (documentation) ──
      if (hostname === DOCS_DOMAIN) {
        if (path === '/') {
          window.location.replace(`https://${DOCS_DOMAIN}/docs`);
          return;
        }
        if (!isDocsRoute(path)) {
          window.location.replace(`https://${MAIN_DOMAIN}${path}${search}`);
          return;
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname, location.search, hostname, navigate]);

  return children;
};

export default DomainGuard;
